import { STATCREW_DATA } from '../constants/routes';
import AuthService from './authservices';

const authService = new AuthService();

export interface StatCrewRequest {
  username: string;
  selectedSport: string;
}

export interface StatCrewRow {
  jobId: string;
  season: string;
  stage: string;
  stageLabel?: string;
  lastUpdated: string;
  owner: string;
  nextAction: string;
  actionLabel: string;
  actionUrl?: string;
}

export interface StatCrewData {
  seasons: string[];
  nextProcessableSeason?: string | null;
  seasonOrder?: Record<string, SeasonOrderEntry>;
  seasonOrderEnforced?: boolean;
  rowsBySeason: Record<string, StatCrewRow[]>;
}

export interface SeasonOrderEntry {
  status: 'complete' | 'processable' | 'locked';
  blockedBy?: string[];
}

interface ApiRow {
  jobId: string;
  season: string;
  stage: string;
  stageLabel?: string;
  awaitingConfirmation?: boolean;
  hasConfirm?: boolean;
  lastUpdated: string;
  owner: string;
  nextAction: string;
  action: {
    label: string;
    url?: string;
  };
}

interface ApiResponse {
  seasons: string[];
  nextProcessableSeason?: string | null;
  seasonOrder?: Record<string, SeasonOrderEntry>;
  seasonOrderEnforced?: boolean;
  data: ApiRow[];
}

function uploadUrlForJob(
  ctx: { username: string; selectedSport: string },
  item: ApiRow
): string {
  return `/dashboard/statcrew/upload?${new URLSearchParams({
    username: ctx.username,
    selectedSport: ctx.selectedSport,
    season: String(item.season),
    jobId: item.jobId,
  }).toString()}`;
}

/**
 * Prefer backend lifecycle mapping; apply defensive overrides only when API omits new fields.
 */
export function normalizeStatCrewHistoryRow(
  item: ApiRow,
  ctx: { username: string; selectedSport: string }
): StatCrewRow {
  const uploadUrl = uploadUrlForJob(ctx, item);
  const previewUrl = `/dashboard/statcrew/jobs/${item.jobId}`;

  const awaiting = item.awaitingConfirmation === true;
  const hasConfirm = item.hasConfirm === true;
  const raw = String(item.stage ?? '')
    .trim()
    .replace(/^LifecycleState\./i, '')
    .toUpperCase()
    .replace(/\s+/g, '_');

  const base: StatCrewRow = {
    jobId: item.jobId,
    season: String(item.season),
    stage: item.stage,
    stageLabel: item.stageLabel,
    lastUpdated: item.lastUpdated,
    owner: item.owner,
    nextAction: item.nextAction,
    actionLabel: item.action.label,
    actionUrl: item.action.url,
  };

  if (raw === 'INSERTED_AVAILABLE') {
    return {
      ...base,
      stageLabel: 'Complete',
      nextAction: 'Ready — data ingested.',
      actionLabel: 'View Preview',
      actionUrl: item.action.url?.includes('/jobs/') ? item.action.url : previewUrl,
    };
  }

  if (item.stageLabel && item.nextAction && item.action?.label) {
    if (item.action.label === 'View Preview' && !base.actionUrl) {
      return { ...base, actionUrl: previewUrl };
    }
    return base;
  }

  if (awaiting) {
    return {
      ...base,
      stageLabel: 'Draft',
      nextAction: 'Resume schedule and roster review.',
      actionLabel: 'Resume',
      actionUrl: item.action.url || uploadUrl,
    };
  }

  if (raw === 'ISSUES_DURING_PROCESSING') {
    if (hasConfirm) {
      return {
        ...base,
        stageLabel: "Couldn't process",
        nextAction:
          "We couldn't process this upload. Please contact support if this persists.",
        actionLabel: "Couldn't process",
        actionUrl: undefined,
      };
    }
    return {
      ...base,
      stageLabel: 'Needs attention',
      nextAction: 'Fix validation issues in the upload wizard.',
      actionLabel: 'Fix issues',
      actionUrl: item.action.url || uploadUrl,
    };
  }

  if (raw === 'NEEDS_MANUAL_VERIFICATION' || raw === 'READY_TO_REVIEW') {
    return {
      ...base,
      stageLabel: 'Review required',
      nextAction: 'Review staged data before integration.',
      actionLabel: 'Review',
      actionUrl: item.action.url || `/dashboard/statcrew/review?jobId=${item.jobId}`,
    };
  }

  if (
    raw === 'VALIDATION_PHASE' ||
    raw === 'IN_PROGRESS' ||
    raw === 'READY_TO_INSERT' ||
    (raw === 'UPLOADED' && hasConfirm) ||
    raw === 'AVAILABLE'
  ) {
    return {
      ...base,
      stageLabel: 'Processing',
      nextAction: 'Processing — check back shortly.',
      actionLabel: 'Processing',
      actionUrl: undefined,
    };
  }

  if (raw === 'UPLOADED' && !hasConfirm) {
    return {
      ...base,
      stageLabel: 'Draft',
      nextAction: 'Resume schedule and roster review.',
      actionLabel: 'Resume',
      actionUrl: uploadUrl,
    };
  }

  return {
    ...base,
    stageLabel: base.stageLabel || 'Processing',
    nextAction: 'Processing — check back shortly.',
    actionLabel: 'Processing',
    actionUrl: undefined,
  };
}

export const fetchStatCrewData = async ({
  username,
  selectedSport,
}: StatCrewRequest): Promise<StatCrewData> => {
  const requestUrl = new URL(STATCREW_DATA);
  requestUrl.searchParams.set('username', username);
  requestUrl.searchParams.set('selectedSport', selectedSport);

  const response = await authService.authenticatedFetch(requestUrl.toString(), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch StatCrew data');
  }

  const apiData: ApiResponse = await response.json();

  const rowsBySeason: Record<string, StatCrewRow[]> = Object.fromEntries(
    apiData.seasons.map((season) => [season, [] as StatCrewRow[]])
  );

  // Select one row per season:
  // - prefer INSERTED_AVAILABLE jobs (completed/integrated)
  // - among jobs of the same priority, pick the most recently updated
  const selectedBySeason: Record<string, StatCrewRow> = {};

  const toTime = (value: string) => {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : 0;
  };

  const isComplete = (row: StatCrewRow) => row.stage === 'INSERTED_AVAILABLE';

  for (const item of apiData.data) {
    const row = normalizeStatCrewHistoryRow(item, { username, selectedSport });
    const existing = selectedBySeason[item.season];
    if (!existing) {
      selectedBySeason[item.season] = row;
      continue;
    }
    if (isComplete(row) && !isComplete(existing)) {
      selectedBySeason[item.season] = row;
    } else if (isComplete(row) === isComplete(existing)) {
      if (toTime(row.lastUpdated) > toTime(existing.lastUpdated)) {
        selectedBySeason[item.season] = row;
      }
    }
  }

  for (const [season, row] of Object.entries(selectedBySeason)) {
    if (!rowsBySeason[season]) rowsBySeason[season] = [];
    rowsBySeason[season] = [row];
  }

  const seasons = [...apiData.seasons];
  for (const season of Object.keys(selectedBySeason)) {
    if (!seasons.includes(season)) {
      seasons.push(season);
    }
  }
  seasons.sort((a, b) => Number(b) - Number(a));

  return {
    seasons,
    nextProcessableSeason: apiData.nextProcessableSeason ?? null,
    seasonOrder: apiData.seasonOrder ?? {},
    seasonOrderEnforced: apiData.seasonOrderEnforced ?? true,
    rowsBySeason,
  };
};
